import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  customerService,
  Customer,
  Header,
  Pagination,
  GetCustomerModel,
} from "@shared/ui";

import "./style.css";

const Customers: React.FC = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<GetCustomerModel[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [droneId, setDroneId] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState(1);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (droneId === "") {
      customerService
        .getCustomers(currentPage, pageSize)
        .then((data) => {
          setCustomers(data.items);
          setTotalPages(data.totalPageCount);
        })
        .catch((error) => {
          console.error("Failed to get customer", error);
        });

      return;
    }

    if (!droneId?.trim()) return;

    customerService
      .findCustomerByDroneId(droneId, currentPage, pageSize)
      .then((data) => {
        setCustomers(data.items);
        setTotalPages(data.totalPageCount);
      })
      .catch((error) => {
        setCustomers([]);
        setTotalPages(0);
        console.error("Failed to find customer by drone ID", error);
      });
  }, [droneId, currentPage, pageSize]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const showAddDialogue = () => {
    setShowAddModal(true);
  };

  const closeModal = () => {
    reset();
    setShowAddModal(false);
  };

  const deleteCustomer = (customerId: number) => {
    customerService
      .deleteCustomer(customerId)
      .then((data) => {
        setCustomers(data.items);
        setTotalPages(data.totalPageCount);
      })
      .catch();
  };

  const addCustomer = (data: FieldValues) => {
    customerService
      .addCustomer({ name: data.name }) //TODO add field value
      .then((data) => {
        setCustomers(data.items);
        setTotalPages(data.totalPageCount);
      })
      .catch();
    reset();
    setShowAddModal(false);
  };

  return (
    <div className="container">
      <Header />
      <div className="customers-header-wrapper">
        <button className="btn btn-primary" onClick={showAddDialogue}>
          {t("customers.buttons.addCustomer")}
        </button>
        <div className="input-wrapper-find" style={{}}>
          <input
            type="text"
            name="find_drone"
            value={droneId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDroneId(event.target.value);
              setCurrentPage(1);
            }}
            placeholder={t("customers.searchPlaceholder")}
          />
          {droneId && (
            <button type="button" onClick={() => setDroneId("")} style={{}}>
              ✕
            </button>
          )}
        </div>
      </div>
      {customers.length ? (
        <>
          <ul className="user-list-wrapper">
            {customers.map((customer, index) => {
              return (
                <Customer
                  key={index}
                  data={customer}
                  onDelete={deleteCustomer}
                />
              );
            })}
          </ul>

          <div className="pagination-wrapper">
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        <h3>{t("customers.noCustomers")}</h3>
      )}

      {showAddModal && (
        <div className="modal-wrapper">
          <dialog open onCancel={closeModal}>
            <h4>{t("customers.modal.title")}</h4>
            <form
              onSubmit={handleSubmit((data) => {
                addCustomer(data);
              })}
            >
              <div>
                <label>{t("customers.modal.label")}</label>
                <input
                  {...register("name", { required: true, maxLength: 100 })}
                />
              </div>
              <div className="text-center mt-20">
                <button className="btn btn-primary" type="submit">
                  {t("customers.modal.addButton")}
                </button>
                <button className="btn btn-secondary" onClick={closeModal}>
                  {t("customers.modal.closeButton")}
                </button>
              </div>
            </form>
          </dialog>
        </div>
      )}
    </div>
  );
};

export default Customers;
